<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { AlertCircle, Check, ChevronsUpDown, Loader2 } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { API, HOST } from '@/utils/http'

const props = defineProps<{
  customerId: number
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'cancel'): void
}>()

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]

// Form state
const loading = ref(false)
const error = ref<string | null>(null)
const stateOpen = ref(false)

// Form fields
const accountOwnerName = ref('')
const bankName = ref('')
const routingNumber = ref('')
const accountNumber = ref('')
const accountType = ref('checking')
const streetLine1 = ref('')
const streetLine2 = ref('')
const city = ref('')
const state = ref('')
const postalCode = ref('')

const handleSubmit = async () => {
  error.value = null
  loading.value = true

  try {
    await API.post(`${HOST}/payments/bridge/customers/${props.customerId}/external-accounts`, {
      account_owner_name: accountOwnerName.value,
      bank_name: bankName.value || undefined,
      account_type: 'us',
      currency: 'usd',
      account: {
        account_number: accountNumber.value,
        routing_number: routingNumber.value,
        checking_or_savings: accountType.value
      },
      address: {
        street_line_1: streetLine1.value,
        street_line_2: streetLine2.value || undefined,
        city: city.value,
        state: state.value,
        postal_code: postalCode.value,
        country: 'USA'
      }
    })

    emit('success')
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to create external account. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="bg-white border rounded-lg p-4 space-y-4 max-w-[600px]">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">Connect External Bank Account</h3>
      <Button variant="ghost" size="sm" @click="emit('cancel')" :disabled="loading">Cancel</Button>
    </div>

    <!-- Error message -->
    <div v-if="error" class="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
      <AlertCircle class="h-4 w-4 flex-shrink-0" />
      <span>{{ error }}</span>
    </div>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <!-- Account Owner Name -->
      <div class="space-y-2">
        <Label for="account-owner" class="text-xs">Account Owner Name (LLC/Company Name) *</Label>
        <Input
          id="account-owner"
          v-model="accountOwnerName"
          placeholder="e.g., Acme LLC"
          required
          :minlength="3"
          :maxlength="35"
          :disabled="loading"
          class="text-sm"
        />
      </div>

      <!-- Bank Name -->
      <div class="space-y-2">
        <Label for="bank-name" class="text-xs">Bank Name</Label>
        <Input
          id="bank-name"
          v-model="bankName"
          placeholder="e.g., Wells Fargo"
          :maxlength="256"
          :disabled="loading"
          class="text-sm"
        />
      </div>

      <!-- Routing, Account Number, Account Type -->
      <div class="grid grid-cols-[1fr_1fr_auto] gap-3">
        <div class="space-y-2">
          <Label for="routing" class="text-xs">Routing Number *</Label>
          <Input
            id="routing"
            v-model="routingNumber"
            placeholder="9 digits"
            required
            pattern="[0-9]{9}"
            :minlength="9"
            :maxlength="9"
            :disabled="loading"
            class="text-sm font-mono"
          />
        </div>
        <div class="space-y-2">
          <Label for="account" class="text-xs">Account Number *</Label>
          <Input
            id="account"
            v-model="accountNumber"
            placeholder="Account number"
            required
            :minlength="1"
            :disabled="loading"
            class="text-sm font-mono"
          />
        </div>
        <div class="space-y-2">
          <Label for="account-type" class="text-xs">Type *</Label>
          <Select v-model="accountType" :disabled="loading">
            <SelectTrigger id="account-type" class="text-sm w-28">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="checking">Checking</SelectItem>
              <SelectItem value="savings">Savings</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <!-- Address Section -->
      <div class="pt-2 border-t">
        <p class="text-xs font-semibold mb-3 text-muted-foreground">Beneficiary Address</p>

        <!-- Street Address -->
        <div class="space-y-2 mb-3">
          <Label for="street1" class="text-xs">Street Address *</Label>
          <Input
            id="street1"
            v-model="streetLine1"
            placeholder="e.g., 123 Main St (must include number)"
            required
            :minlength="4"
            :maxlength="35"
            :disabled="loading"
            class="text-sm"
          />
        </div>

        <div class="space-y-2 mb-3">
          <Label for="street2" class="text-xs">Apartment, Suite, etc.</Label>
          <Input
            id="street2"
            v-model="streetLine2"
            placeholder="Optional"
            :maxlength="35"
            :disabled="loading"
            class="text-sm"
          />
        </div>

        <!-- City, State, ZIP -->
        <div class="grid grid-cols-[1fr_auto_auto] gap-3">
          <div class="space-y-2">
            <Label for="city" class="text-xs">City *</Label>
            <Input
              id="city"
              v-model="city"
              placeholder="e.g., San Francisco"
              required
              :disabled="loading"
              class="text-sm"
            />
          </div>

          <div class="space-y-2">
            <Label class="text-xs">State *</Label>
            <Popover v-model:open="stateOpen">
              <PopoverTrigger as-child>
                <Button
                  variant="outline"
                  role="combobox"
                  :aria-expanded="stateOpen"
                  :disabled="loading"
                  class="w-24 justify-between text-sm font-normal"
                >
                  {{ state || 'State' }}
                  <ChevronsUpDown class="ml-1 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-40 p-0">
                <Command>
                  <CommandInput placeholder="Search..." />
                  <CommandList>
                    <CommandEmpty>No state found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        v-for="code in US_STATES"
                        :key="code"
                        :value="code"
                        @select="() => { state = code; stateOpen = false }"
                      >
                        <Check :class="cn('mr-2 h-4 w-4', state === code ? 'opacity-100' : 'opacity-0')" />
                        {{ code }}
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div class="space-y-2">
            <Label for="postal" class="text-xs">ZIP Code *</Label>
            <Input
              id="postal"
              v-model="postalCode"
              placeholder="e.g., 94102"
              required
              :disabled="loading"
              class="text-sm w-28"
            />
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" @click="emit('cancel')" :disabled="loading">
          Cancel
        </Button>
        <Button type="submit" :disabled="loading">
          <Loader2 v-if="loading" class="h-4 w-4 mr-2 animate-spin" />
          Connect Account
        </Button>
      </div>
    </form>
  </div>
</template>
